// src/lib/welding.js
// The Welding page's data layer — #746, the Welding gate
// (docs/gates/Stede_Welding_Gate_746.html), Session 79.22b.6.2d.
//
// ── What the page is, in plain words ──
//
// Houston makes a frame in two steps by two crews. The welders weld the raw
// steel; powder coating coats it and signs it off on To make, which is what
// lowers the official count. The welders need one place that says how many of
// each size to weld, and a way to record what they welded without touching To
// make (746 D1). This file is everything between the database and that page:
// the numbers (weldingDemand), the reads (loadWeldingBoard, loadSizeToday) and
// the button calls. Who may press what is permissions.js (canSeeWelding and its
// five siblings), held to the database by weldingRules746.test.js.
//
// ── The numbers are To make's own, composed once (the gate's F5) ──
//
// To make's composition already exists twice - the Transfers page and Stock
// Home's work row (branchWorkLists() in transferWork.js) - and this page must
// not be a third copy written by hand. So weldingDemand() is built only from To
// make's own functions: productionQueue, makeProgress, openOwnShelfFor,
// reserveCandidates, productionRows, rowShortBy and promisedDateFor, with To
// make's own day rule (localDay, addDays). weldingDemand.test.js feeds one set
// of transfers to it and to branchWorkLists() and holds the totals by size
// equal (#512's lesson).

import { supabase } from './supabase';
import { productionQueue, makeProgress, promisedDateFor, rowShortBy, transfersInHand } from './branchTransfers';
import { reserveCandidates, productionRows, fetchProductionData } from './production';
import { openOwnShelfFor } from './transferWork';
import { localDay, addDays } from './transferSortFilter';
import { cutOff } from './readLimit';

const num = v => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// ─────────────────────────────────────────────────────────────────────────────
// THE NUMBERS
// ─────────────────────────────────────────────────────────────────────────────

// A line's own date (746 D21): its promised date, else the transfer's
// confirmed date (promisedDateFor), else its needed-by date. All three can be
// empty, and then the line is No date.
export function lineDay(line, transfer) {
  const d = promisedDateFor(line, transfer) || transfer?.needed_by || null;
  return d ? String(d).slice(0, 10) : null;
}

// Its WELD-BY date (746 D26): the line's date minus Days to coat and ship, in
// calendar days. With 0 it is the line's own date, exactly as To make dates it.
export function weldByDay(day, coatShipDays) {
  if (!day) return null;
  const days = Math.max(num(coatShipDays) ?? 0, 0);
  return days ? addDays(day, -days) : day;
}

// Where a weld-by date falls against the device's date (746 D7, D26):
// before today Late; today Today; tomorrow to the look-ahead's N days after
// today Next N days; later still Later, shown and never counted; no date No
// date. To make's own boundaries (dueMatches() in transferSortFilter.js).
export function bucketOf(weldBy, today, horizonDays) {
  if (!weldBy) return 'nodate';
  if (weldBy < today) return 'late';
  if (weldBy === today) return 'today';
  return weldBy <= addDays(today, Math.max(num(horizonDays) ?? 0, 0)) ? 'next' : 'later';
}

// The order a need is covered in, and the order the list sorts by (746 D33):
// dated lines from the earliest weld-by date (which spends welded, waiting on
// Late first, then Today, then the next N days), then No date, then the shelf
// top-up, then the reserve. A size whose needs are all covered sorts after
// every size still short; a size with nothing counted at all, after that.
// `later` and `waiting` are the board's two undrawn edges (the gate's F1 state
// table, Session 79.22b.6.4.1): a size whose orders are all beyond the
// look-ahead sorts after every size with something counted, and a size with
// frames welded and nothing on order sorts last, "so the frames are never
// invisible". Both are still `idle` to the every-size reserve list.
const GROUP = { line: 0, nodate: 1, shelf: 2, reserve: 3, covered: 4, later: 5, waiting: 6, idle: 7 };

// EVERY WELDED SIZE AT ONE BRANCH, AND WHAT IS STILL TO WELD FOR EACH.
//
//   transfers     the work in hand narrowed to the branch (the board's read 1)
//   items, stock, categories   fetchProductionData()'s three lists (read 2)
//   board         welding_board()'s rows: every active size the branch welds,
//                 with welded, waiting as the database works it out (read 3)
//   reserves      current_weld_reserves()'s rows (read 6); a size with none is 0
//   horizonDays, coatShipDays  the two company settings (read 5)
//   today         the device's date, YYYY-MM-DD (To make's own "today")
//
// Every size the board names comes back, even with nothing to weld: the
// every-size reserve list (R3, R4) is built from the same rows. `state` says
// which kind it is - 'due' (still to weld), 'covered' (something is counted
// and all of it is covered, 746 D33), or 'idle' (nothing counted at all) - so
// the board can show the first two and the every-size list all three.
export function weldingDemand({
  transfers = [], items = [], stock = [], categories = [], board = [], reserves = [],
  branchId, horizonDays, coatShipDays = 0, today = localDay(),
} = {}) {
  if (branchId == null) return { sizes: [], stillToWeld: 0, counted: 0, everythingCovered: false, nothingCounted: true };

  const welded = new Map((Array.isArray(board) ? board : [])
    .map(b => [String(b.item_id), num(b.welded_waiting) ?? 0]));
  const itemById = new Map((Array.isArray(items) ? items : []).map(i => [String(i.id), i]));
  const reserveById = new Map((Array.isArray(reserves) ? reserves : [])
    .map(r => [String(r.item_id), r]));

  // To make's own rows, exactly as the Transfers page and Stock Home build
  // them: other branches' orders, our own shelf's requests, and the low-shelf
  // rows - an item's low-shelf row stepping aside while a request for our own
  // shelf names it (746 D8, productionRows()).
  const all = Array.isArray(transfers) ? transfers : [];
  const orders = productionQueue(all, branchId);
  const lowShelf = reserveCandidates({ items, stock, categories, branchId });
  const rows = productionRows({ orders, reserve: lowShelf, openOwnShelf: openOwnShelfFor(all, branchId) });

  const sizes = new Map();
  const sizeOf = id => {
    const key = String(id);
    if (!sizes.has(key)) {
      sizes.set(key, { lines: [], shelf: null });
    }
    return sizes.get(key);
  };
  for (const key of welded.keys()) sizeOf(key);

  for (const row of rows) {
    if (row.kind === 'order') {
      const t = row.transfer;
      for (const line of t.branch_transfer_items || []) {
        if (!welded.has(String(line.item_id))) continue;
        // makeProgress() over one line: what nobody has built yet, and a
        // refused line is 0 (#577).
        const qty = makeProgress([line]).toMake;
        if (qty <= 0) continue;
        const day = lineDay(line, t);
        const weldBy = weldByDay(day, coatShipDays);
        sizeOf(line.item_id).lines.push({
          transferId: t.id,
          transferNumber: t.transfer_number ?? null,
          lineId: line.id ?? null,
          own: !!row.own,
          qty,
          day,
          weldBy,
          bucket: bucketOf(weldBy, today, horizonDays),
        });
      }
    } else if (row.kind === 'reserve' && welded.has(String(row.item?.id))) {
      // The shelf top-up (746 D8): #615's shortfall to the reorder point,
      // rowShortBy() - 0 exactly at the point.
      sizeOf(row.item.id).shelf = {
        qty: rowShortBy({ reorder_point: row.reorderPoint, qty_on_hand: row.qtyOnHand }) ?? 0,
        qtyOnHand: row.qtyOnHand,
        reorderPoint: row.reorderPoint,
      };
    }
  }

  const out = [];
  for (const [key, s] of sizes) {
    if (!welded.has(key)) continue;   // only sizes the branch welds (746 D3)
    const sum = b => s.lines.filter(l => l.bucket === b).reduce((n, l) => n + l.qty, 0);
    const res = reserveById.get(key);
    const reserve = Math.max(num(res?.reserve) ?? 0, 0);
    const shelfTopUp = s.shelf ? s.shelf.qty : 0;
    const raw = welded.get(key);
    const waiting = Math.max(raw, 0);   // below zero is shown as 0 with a note (746 D12)
    const late = sum('late');
    const todayQty = sum('today');
    const next = sum('next');
    const noDate = sum('nodate');
    const later = sum('later');
    const counted = late + todayQty + next + noDate + shelfTopUp + reserve;
    const stillToWeld = Math.max(counted - waiting, 0);

    // WHAT WELDED, WAITING COVERS, IN ORDER (746 D33): spend it on the dated
    // lines from the earliest weld-by date, then No date, then the shelf
    // top-up, then the reserve. The first need it does not fully cover is the
    // size's earliest uncovered need.
    const needs = [
      ...s.lines.filter(l => l.bucket !== 'later' && l.bucket !== 'nodate')
        .sort((a, b) => (a.weldBy < b.weldBy ? -1 : a.weldBy > b.weldBy ? 1 : 0))
        .map(l => ({ kind: 'line', qty: l.qty, weldBy: l.weldBy })),
      { kind: 'nodate', qty: noDate },
      { kind: 'shelf', qty: shelfTopUp },
      { kind: 'reserve', qty: reserve },
    ].filter(n => n.qty > 0);
    let left = waiting;
    let earliestNeed = null;
    for (const n of needs) {
      if (left >= n.qty) { left -= n.qty; continue; }
      earliestNeed = n.kind === 'line' ? { kind: 'line', weldBy: n.weldBy } : { kind: n.kind };
      break;
    }

    const state = counted === 0 ? 'idle' : stillToWeld === 0 ? 'covered' : 'due';
    // ON THE BOARD (the gate's F1 state table, 79.22b.6.4.1): every size with
    // something counted, and two kinds with nothing counted that the board
    // still shows - only Later orders ("Later 50, not counted"), and frames
    // welded and waiting with nothing on order. Anything else is for the
    // every-size reserve list alone.
    const edge = state !== 'idle' ? null : later > 0 ? 'later' : waiting > 0 ? 'waiting' : null;
    const onBoard = state !== 'idle' || edge != null;
    const item = itemById.get(key) || null;
    out.push({
      itemId: num(key) ?? key,
      item,
      name: item?.name || '',
      weldedWaiting: raw,
      waitingShown: waiting,
      belowZero: raw < 0,
      late, today: todayQty, next, noDate, later,
      shelfTopUp,
      shelf: s.shelf,
      reserve,
      reserveSetBy: res?.set_by_name ?? null,
      reserveSetAt: res?.set_at ?? null,
      counted,
      stillToWeld,
      state,
      covered: state === 'covered',
      edge,
      onBoard,
      // The Late chip shows only while the earliest uncovered need is a line
      // whose weld-by date is before today (746 D33).
      lateChip: !!earliestNeed && earliestNeed.kind === 'line' && earliestNeed.weldBy < today,
      earliestNeed,
      lines: s.lines,
    });
  }

  const group = s => (s.state === 'due' ? GROUP[s.earliestNeed.kind] : GROUP[s.edge || s.state]);
  out.sort((a, b) => {
    const g = group(a) - group(b);
    if (g) return g;
    if (group(a) === GROUP.line && a.earliestNeed.weldBy !== b.earliestNeed.weldBy) {
      return a.earliestNeed.weldBy < b.earliestNeed.weldBy ? -1 : 1;
    }
    return a.name.localeCompare(b.name) || String(a.itemId).localeCompare(String(b.itemId));
  });

  const stillToWeld = out.reduce((n, s) => n + s.stillToWeld, 0);
  const counted = out.reduce((n, s) => n + s.counted, 0);
  return {
    sizes: out,
    stillToWeld,
    counted,
    // "Welded frames and stakes cover the next 7 days and every reserve."
    everythingCovered: counted > 0 && stillToWeld === 0,
    nothingCounted: counted === 0,
    // "Nothing to weld. No orders are open, no shelf is low, and no size has a
    // reserve." (S3) - the gate's rule is wider than nothing counted: nothing
    // Later and nothing welded, waiting either (Session 79.22b.6.4.1), so the
    // sentence never stands over a size the board is showing.
    nothingToWeld: !out.some(s => s.onBoard),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// WHICH BRANCH THE PAGE OPENS ON (746 D23) - Session 79.22b.6.3
// ─────────────────────────────────────────────────────────────────────────────

// The person's own branch when it welds. Otherwise - an admin or an executive
// whose own branch welds nothing - the first branch that does, which is Houston
// today, so a Dallas admin is not greeted with "nothing is welded here" on every
// visit; the branch chip switches to any branch (built at 79.22b.6.4). null
// until the sign-in has heard which branches weld (auth.weldingBranchIds, in
// the database's own order), and for anyone else, whom the page rule turns away
// (canSeeWelding()). Takes the whole `auth` from useAuth(), as every rule does.
export function weldingBranchFor(auth) {
  const ids = Array.isArray(auth?.weldingBranchIds)
    ? auth.weldingBranchIds.map(num).filter(id => id != null)
    : [];
  if (!ids.length) return null;
  const own = num(auth?.profile?.branch_id);
  if (own != null && ids.includes(own)) return own;
  if (!(auth?.isAdmin || auth?.isExecutive)) return null;
  return Math.min(...ids);
}

// ─────────────────────────────────────────────────────────────────────────────
// THE READS - the gate's B3, "Load the board"
// ─────────────────────────────────────────────────────────────────────────────

// The work in hand, narrowed to the welding branch's own sending work: the
// columns To make's own functions read, and nothing more (B3, read 1).
export const BOARD_TRANSFER_SELECT =
  'id, from_branch_id, to_branch_id, status, needed_by, confirmed_date, requested_at, transfer_number, '
  + 'branch_transfer_items(id, item_id, qty_requested, qty_ready, line_status, promised_date)';
export const BOARD_STATUSES = ['awaiting_confirmation', 'approved', 'in_transit'];

// The device's own midnight, which keeps "today" in one place: the flags shown
// (746 D15), today's entries on a size's sheet (746 D20) and start_weld_flag()'s
// p_day_start all read it.
export function deviceDayStart(now = new Date()) {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

// A read that failed, or arrived cut at the database's 1,000 rows (cutOff()).
const bad = r => !r || !!r.error || cutOff(r);

// EVERYTHING THE BOARD SHOWS, IN ONE GO - six reads in parallel (B3). ANY READ
// THAT FAILS OR COMES BACK CUT IS THE ERROR STATE (S2): never a short list
// shown as whole, and never a list counted with a default setting (X2). The
// answer carries the six reads' rows and the numbers worked out from them for
// the device's date, so the page can work them out again when the day turns.
export async function loadWeldingBoard(branchId, { today = localDay() } = {}) {
  if (branchId == null) return { ok: false, reason: 'no_branch' };
  const [work, prod, board, flags, settings, reserves] = await Promise.all([
    transfersInHand(BOARD_TRANSFER_SELECT).eq('from_branch_id', branchId).in('status', BOARD_STATUSES),
    // #608's guard: a cut or failed catalog read answers ok: false.
    fetchProductionData(branchId),
    supabase.rpc('welding_board', { p_branch_id: branchId }, { count: 'exact' }),
    supabase.from('weld_flags')
      .select('id, branch_id, item_id, user_id, user_name, started_at', { count: 'exact' })
      .eq('branch_id', branchId)
      .is('ended_at', null),
    supabase.from('company_settings').select('welding_horizon_days, welding_coat_ship_days').maybeSingle(),
    supabase.rpc('current_weld_reserves', { p_branch_id: branchId }, { count: 'exact' }),
  ]);

  const failed = [
    ['work in hand', bad(work)],
    ['catalog', !prod || !prod.ok],
    ['welded, waiting', bad(board)],
    ['flags', bad(flags)],
    ['settings', !settings || !!settings.error
      || num(settings.data?.welding_horizon_days) == null || num(settings.data?.welding_coat_ship_days) == null],
    ['reserves', bad(reserves)],
  ].filter(([, isBad]) => isBad).map(([what]) => what);
  // A read that ARRIVED but was cut at the database's 1,000 rows is said in its
  // own words (S2's table row, Session 79.22b.6.4.1): trying again would bring
  // the same cut list, so the answer is why, not Try again.
  const cut = [work, board, flags, reserves].some(r => r && !r.error && cutOff(r)) || !!prod?.cut;
  if (failed.length) return { ok: false, reason: cut ? 'cut' : 'read', failed };

  const horizonDays = num(settings.data.welding_horizon_days);
  const coatShipDays = num(settings.data.welding_coat_ship_days);
  const rows = {
    transfers: work.data || [],
    items: prod.items,
    stock: prod.stock,
    categories: prod.categories,
    board: board.data || [],
    reserves: reserves.data || [],
  };
  return {
    ok: true,
    ...rows,
    flags: flags.data || [],
    horizonDays,
    coatShipDays,
    demand: weldingDemand({ ...rows, branchId, horizonDays, coatShipDays, today }),
  };
}

// The flags a size shows (746 D15): open, raised since the device's midnight -
// a flag from yesterday is simply not shown - and none on a size that is
// Covered, whose flags hide rather than end. Oldest first, so the first name
// shown is the person who started.
export function flagsShown(flags, { sizes = [], dayStart = deviceDayStart() } = {}) {
  const covered = new Set(sizes.filter(s => s.state !== 'due').map(s => String(s.itemId)));
  const out = new Map();
  for (const f of Array.isArray(flags) ? flags : []) {
    if (f.ended_at != null || covered.has(String(f.item_id))) continue;
    if (!f.started_at || new Date(f.started_at) < dayStart) continue;
    const key = String(f.item_id);
    if (!out.has(key)) out.set(key, []);
    out.get(key).push(f);
  }
  for (const list of out.values()) list.sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
  return out;
}

// A SIZE'S SHEET: today's entries (746 D20, "10 welded · Tess Ivory ·
// 10:12 am"), read when the sheet opens - records, Undos and counts since the
// device's midnight, newest first. The same error rule as the board's.
export async function loadSizeToday(branchId, itemId, { dayStart = deviceDayStart() } = {}) {
  if (branchId == null || itemId == null) return { ok: false, reason: 'no_size' };
  const since = dayStart.toISOString();
  const [records, counts] = await Promise.all([
    supabase.from('weld_records')
      .select('id, qty, reverses_id, recorded_by, recorded_by_name, recorded_at', { count: 'exact' })
      .eq('branch_id', branchId).eq('item_id', itemId).gte('recorded_at', since)
      .order('recorded_at', { ascending: false }),
    supabase.from('weld_counts')
      .select('id, counted_qty, previous_qty, counted_by, counted_by_name, counted_at', { count: 'exact' })
      .eq('branch_id', branchId).eq('item_id', itemId).gte('counted_at', since)
      .order('counted_at', { ascending: false }),
  ]);
  if (bad(records) || bad(counts)) return { ok: false, reason: 'read' };
  const entries = [
    ...(records.data || []).map(r => ({
      kind: r.reverses_id == null ? 'record' : 'undo', id: r.id, qty: r.qty, reverses: r.reverses_id,
      by: r.recorded_by, byName: r.recorded_by_name, at: r.recorded_at,
    })),
    ...(counts.data || []).map(c => ({
      kind: 'count', id: c.id, qty: c.counted_qty, previous: c.previous_qty,
      by: c.counted_by, byName: c.counted_by_name, at: c.counted_at,
    })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at) || Number(b.id) - Number(a.id));
  return { ok: true, entries };
}

// ─────────────────────────────────────────────────────────────────────────────
// THE BUTTON CALLS - one database step each (B3)
//
// B3'S RULE: AN ANSWER WITHOUT THE ID THAT PROVES A WRITE IS NOT A WRITE. "No
// record_id means nothing was recorded; a null error alone is not proof." So
// each call returns { ok: true, ... } only when the step names what it wrote
// (or, for Stop, Clear and a reserve already at its number, says plainly that
// nothing needed writing). A refusal comes back as the database's own words,
// which are the gate's F2 words. Every step checks who is pressing on its own;
// these calls never decide that (permissions.js decides what is drawn).
// ─────────────────────────────────────────────────────────────────────────────

const NOT_SAVED = "Stede couldn't save that. Nothing was changed. Try again.";
const notSaved = () => ({ error: new Error(NOT_SAVED) });

// Record N welded (746 D13). Answers { recordId, qty, weldedWaiting }.
export async function recordWelds({ branchId, itemId, qty }) {
  const { data, error } = await supabase.rpc('record_welds', { p_branch_id: branchId, p_item_id: itemId, p_qty: qty });
  if (error) return { error };
  if (data?.record_id == null) return notSaved();
  return { ok: true, recordId: data.record_id, qty: num(data.qty), weldedWaiting: num(data.welded_waiting) };
}

// Undo, on the answer (746 D13, D20). Answers { undoId, weldedWaiting }.
export async function undoWeldRecord(recordId) {
  const { data, error } = await supabase.rpc('undo_weld_record', { p_record_id: recordId });
  if (error) return { error };
  if (data?.undo_id == null) return notSaved();
  return { ok: true, undoId: data.undo_id, weldedWaiting: num(data.welded_waiting) };
}

// Set it to N - counting what is waiting (746 D14). Answers { countId,
// counted, previous }; previous is the database's own figure (and can be
// below zero, 746 D12).
export async function countWelded({ branchId, itemId, counted }) {
  const { data, error } = await supabase.rpc('count_welded', { p_branch_id: branchId, p_item_id: itemId, p_counted: counted });
  if (error) return { error };
  if (data?.count_id == null) return notSaved();
  return { ok: true, countId: data.count_id, counted: num(data.counted), previous: num(data.previous) };
}

// I'm on it · Help on this (746 D2, D15). Answers { flagId, movedFromItemId }.
export async function startWeldFlag({ branchId, itemId, dayStart = deviceDayStart() }) {
  const { data, error } = await supabase.rpc('start_weld_flag', {
    p_branch_id: branchId, p_item_id: itemId, p_day_start: dayStart.toISOString(),
  });
  if (error) return { error };
  if (data?.flag_id == null) return notSaved();
  return { ok: true, flagId: data.flag_id, movedFromItemId: data.moved_from_item_id ?? null };
}

// Stop. Never an error with no flag open: { stopped: false } says so.
export async function stopWeldFlag() {
  const { data, error } = await supabase.rpc('stop_weld_flag');
  if (error) return { error };
  if (typeof data?.stopped !== 'boolean') return notSaved();
  return { ok: true, stopped: data.stopped };
}

// Clear somebody's flag (746 D15). { cleared: false } means it had already
// ended - changed nothing, and says so.
export async function clearWeldFlag(flagId) {
  const { data, error } = await supabase.rpc('clear_weld_flag', { p_flag_id: flagId });
  if (error) return { error };
  if (typeof data?.cleared !== 'boolean') return notSaved();
  return { ok: true, cleared: data.cleared };
}

// Set one size's reserve (746 D29-D31). `was` is the number the sheet opened
// on; if somebody changed it meanwhile the database writes nothing and says so
// (55000). { changed: false } is a reserve already at that number; a change
// with no reserve id is not a change.
export async function setWeldReserve({ branchId, itemId, reserve, was }) {
  const { data, error } = await supabase.rpc('set_weld_reserve', {
    p_branch_id: branchId, p_item_id: itemId, p_reserve: reserve, p_was: was,
  });
  if (error) return { error };
  if (typeof data?.changed !== 'boolean' || (data.changed && data.reserve_id == null)) return notSaved();
  return { ok: true, changed: data.changed, reserveId: data.reserve_id ?? null, reserve: num(data.reserve), previous: num(data.previous) };
}

// Save every size's reserve at once (746 D31): all or nothing, in one step.
// `changes` is [{ itemId, reserve, was }]. Answers { changed, madeTogether };
// 0 changed is a save that needed nothing (madeTogether null).
export async function setWeldReserves({ branchId, changes }) {
  const list = (Array.isArray(changes) ? changes : [])
    .map(c => ({ item_id: Number(c.itemId), reserve: Number(c.reserve), was: Number(c.was) }));
  const { data, error } = await supabase.rpc('set_weld_reserves', { p_branch_id: branchId, p_changes: list });
  if (error) return { error };
  const changed = num(data?.changed);
  if (changed == null || (changed > 0 && data.made_together == null)) return notSaved();
  return { ok: true, changed, madeTogether: data.made_together ?? null };
}
